/* eslint-disable no-param-reassign */
/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import qrcode from 'qrcode';
import { replace } from 'connected-react-router';
import { TFunction } from 'i18next';
import Repos from '../../infrastructure/repositories/Repos';
import {
  LoaAuthenticatorType,
  DEFAULT_EMAIL_DIGITS,
  DEFAULT_SMS_DIGITS,
  I18nNamespaces,
  ValidationErrorKey as ValErr,
  FactorStatus
} from '../../app/shared/utils/constants.util';
import {
  getHttpErrorStatusCode
} from '../../app/shared/utils/errors.util';
import { requireUserId } from '../Profile/profileSlice';
import { AppThunk } from '../../app/store';
import { showToast } from '../../app/shared/AppSlice/AppSlice';
import { ILoaEnrichment } from '../../domain/models/ILoaEnrichment';
import { IGetListQueryParams } from '../../infrastructure/repositories/utils/IGetListQueryParams';
import {
  ISecuritySetup, ISupportedFactor, ISecurityFactor
} from '../../domain/models/ISecuritySetup';
import { SecuritySetupType, AuthErrorKey } from '../../domain/models/impl/SecuritySetup';
import { getSecretKeyLengthFromUri } from '../../app/shared/utils/helpers.util';
import VERIFY_FACTOR_DATA_BY_TYPE from './DerivedFactors/VerifyFactorsByType';
import { markElevationSuccess, markElevationError, getFlowSnapshot } from '../../app/shared/utils/popup.util';
import { SecuritySetupRoutes } from '../../app/shared/utils/securitySetupRoutes';

export interface SelectedFactor {
  factorId: string;
  type: SecuritySetupType;
  digits?: number;
  loaLevel: number;
  data?: string; // can be updated when new methods arrive
}

export const enum EnrichmentStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

export const ENRICHMENT_OUTCOME_KEY = 'enrichment-outcome';

type EnrichmentStatusType = EnrichmentStatus;
export interface ISetupSecurityState {
  supportedFactors: ISupportedFactor[];
  enrolledFactors: ISecurityFactor[];
  selectedFactor: SelectedFactor | undefined;
  pageLoading: boolean;
  actionLoading: boolean;
  error: string;
  qrCode: string;
  validationError: string;
  isQrCodeLoading: boolean;
  secretKey: string;
  enrichmentStatus: EnrichmentStatusType;
  enrichmentError?: string;
  enrichmentLoaProof?: string;
  enrichmentTargetLevel?: number;
  enrichmentReturnUrl?: string;
  factorsLoaded: boolean;
  /** Incremented on enrollment reset; thunks use it to discard stale results. */
  enrollGeneration: number;
}

type VerifyAction = 'activate' | 'verify' | 'challenge';
type ChallengePageFlow = 'sms-activate' | 'sms-verify' | 'email' | 'authenticator';

export const initialState: ISetupSecurityState = {
  supportedFactors: [
    {
      id: 'Authenticator',
      title: 'loaEnrichment.factors.authenticator.titlePreferredAuth',
      securityLevel: 3,
      loaLevel: 2,
      type: SecuritySetupType.AUTHENTICATOR,
      provider: 'PreferredAuthenticator',
      authenticatorType: LoaAuthenticatorType.PreferredAuthenticator,
      authnFactor: 'PREFERRED_AUTHENTICATOR',
      helpText: 'loaEnrichment.factors.authenticator.authenticatorsHelpText'
    },
    {
      id: 'sms',
      title: 'loaEnrichment.factors.sms.title',
      securityLevel: 1,
      loaLevel: 2,
      type: SecuritySetupType.SMS,
      provider: 'Telesign',
      authenticatorType: LoaAuthenticatorType.Telesign,
      authnFactor: 'SMS'
    },
    {
      id: 'email',
      title: 'loaEnrichment.factors.email.title',
      securityLevel: 1,
      loaLevel: 2,
      type: SecuritySetupType.EMAIL,
      provider: 'Notification',
      authenticatorType: LoaAuthenticatorType.NotificationService,
      authnFactor: 'EMAIL'
    }
  ],
  enrolledFactors: [],
  selectedFactor: undefined,
  pageLoading: false,
  actionLoading: false,
  error: '',
  qrCode: '',
  validationError: '',
  isQrCodeLoading: false,
  secretKey: '',
  enrichmentStatus: EnrichmentStatus.Idle,
  enrichmentError: undefined,
  enrichmentLoaProof: undefined,
  enrichmentTargetLevel: undefined,
  factorsLoaded: false,
  enrollGeneration: 0
};

export const setupSecuritySlice = createSlice({
  name: 'setupSecurity',
  initialState,
  reducers: {
    setEnrolledFactors: (state: ISetupSecurityState, action: PayloadAction<ISecurityFactor[]>): void => {
      state.enrolledFactors = action.payload;
    },
    setSelectedFactor: (state, action: PayloadAction<SelectedFactor | undefined>) => {
      state.selectedFactor = action.payload;
    },
    setError: (state: ISetupSecurityState, action: PayloadAction<string>): void => {
      state.error = action.payload;
    },
    setQrCode: (state: ISetupSecurityState, action: PayloadAction<string>): void => {
      state.qrCode = action.payload;
    },
    setSecretKey: (state: ISetupSecurityState, action: PayloadAction<string>) => {
      state.secretKey = action.payload;
    },
    setValidationError: (state: ISetupSecurityState, action: PayloadAction<string>) => {
      state.validationError = action.payload;
    },
    setIsQrCodeLoading: (state: ISetupSecurityState, action: PayloadAction<boolean>) => {
      state.isQrCodeLoading = action.payload;
    },
    setPageLoading: (state: ISetupSecurityState, action: PayloadAction<boolean>): void => {
      state.pageLoading = action.payload;
    },
    setActionLoading: (state: ISetupSecurityState, action: PayloadAction<boolean>): void => {
      state.actionLoading = action.payload;
    },
    setEnrichmentStatus(state, action: PayloadAction<EnrichmentStatus>) {
      state.enrichmentStatus = action.payload;
    },
    setEnrichmentError(state, action: PayloadAction<string | undefined>) {
      state.enrichmentError = action.payload;
    },
    setEnrichmentContext(state, action: PayloadAction<{ loaProof?: string; targetLevel?: number } | undefined>) {
      state.enrichmentLoaProof = action.payload?.loaProof;
      state.enrichmentTargetLevel = action.payload?.targetLevel;
    },
    resetEnrichmentState(state) {
      state.enrichmentStatus = EnrichmentStatus.Idle;
      state.enrichmentError = undefined;
      state.enrichmentLoaProof = undefined;
      state.enrichmentTargetLevel = undefined;
      state.enrichmentReturnUrl = undefined;
    },
    setEnrichmentReturnUrl(state, action: PayloadAction<string | undefined>) {
      state.enrichmentReturnUrl = action.payload;
    },
    setFactorsLoaded(state, action: PayloadAction<boolean>) {
      state.factorsLoaded = action.payload;
    },
    resetEnrollmentState(state) {
      state.selectedFactor = undefined;
      state.actionLoading = false;
      state.error = '';
      state.validationError = '';
      state.qrCode = '';
      state.secretKey = '';
      state.isQrCodeLoading = false;
      state.enrollGeneration += 1;
    },
    resetSetupSecurityState: (): ISetupSecurityState => initialState
  }
});

export const {
  setEnrolledFactors,
  setSelectedFactor,
  setError,
  setQrCode,
  resetSetupSecurityState,
  setValidationError,
  setIsQrCodeLoading,
  setSecretKey,
  setPageLoading,
  setActionLoading,
  setEnrichmentStatus,
  setEnrichmentError,
  setEnrichmentContext,
  setEnrichmentReturnUrl,
  setFactorsLoaded,
  resetEnrichmentState,
  resetEnrollmentState
} = setupSecuritySlice.actions;

function removeQueryParam(search: string | undefined, key: string): string {
  const params = new URLSearchParams(search || '');
  params.delete(key);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/** Check whether the enrollment session has been reset since the thunk started. */
const isStaleSetup = (gen: number, getState: () => any): boolean => (
  getState().setupSecurity.enrollGeneration !== gen
);

export const getAllFactors = (
  userId: string,
  level?: number,
  status?: FactorStatus
): AppThunk<Promise<unknown>> => async (dispatch) => {
  const filter: Record<string, any> = {};

  if (typeof level === 'number') {
    filter.level = level;
  }

  if (status) {
    filter.status = status;
  }

  const queryParams: IGetListQueryParams<ISecuritySetup> = { filter };

  const factorsList = await Repos.instance.securitySetupRepository.getAll(
    queryParams,
    { userId }
  );

  dispatch(setEnrolledFactors((factorsList?.items ?? []) as ISecurityFactor[]));
};

export const loadSecuritySetupPage = (t: TFunction): AppThunk => async (dispatch, getState) => {
  const { factorsLoaded, pageLoading } = getState().setupSecurity;
  if (factorsLoaded || pageLoading) return;

  dispatch(setPageLoading(true));
  try {
    const userId = await dispatch(requireUserId() as any);
    await dispatch(getAllFactors(userId, undefined, 'ACTIVE'));
  } catch (e) {
    const errorStatus = getHttpErrorStatusCode(e) ?? '500';
    if (errorStatus === 403) {
      dispatch(setError(AuthErrorKey.ResourceForbidden));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceForbiddenToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.factorsFetchFailedToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    }
  } finally {
    dispatch(setPageLoading(false));
    dispatch(setFactorsLoaded(true));
  }
};

export const enrollAuthenticator = (factorData: Partial<ISecuritySetup>, t: TFunction): AppThunk => async (dispatch, getState):
  Promise<void> => {
  const gen = getState().setupSecurity.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));
  dispatch(setIsQrCodeLoading(true));

  try {
    const userId = await dispatch(requireUserId() as any);

    const res = await Repos.instance.securitySetupRepository
      .create(factorData, { userId }) as Partial<ISecuritySetup>;

    if (!res || !res.id) {
      throw new Error('Empty Authenticator enroll response');
    }

    if (isStaleSetup(gen, getState)) return;

    const uri = res.options?.uri ?? '';
    const secret = res.options?.secret ?? '';
    const digits = uri ? getSecretKeyLengthFromUri(uri) : 0;
    const loaLevel = res?.level ?? 2;

    dispatch(setSelectedFactor({
      factorId: res.id!,
      type: res.type ?? SecuritySetupType.AUTHENTICATOR,
      digits,
      loaLevel
    }));

    try {
      if (uri) {
        const dataURL = await qrcode.toDataURL(uri);
        dispatch(setQrCode(dataURL));
      } else {
        dispatch(setQrCode(''));
      }
    } catch {
      dispatch(setQrCode(uri || ''));
    } finally {
      dispatch(setIsQrCodeLoading(false));
    }

    if (secret) {
      dispatch(setSecretKey(secret));
    } else {
      dispatch(setSecretKey(''));
    }
  } catch (err) {
    if (isStaleSetup(gen, getState)) return;
    dispatch(setIsQrCodeLoading(false));

    dispatch(setQrCode(''));
    dispatch(setSecretKey(''));

    const errorStatus = getHttpErrorStatusCode(err) ?? '500';
    if (errorStatus === 409) {
      dispatch(setError(AuthErrorKey.ResourceAlreadyExists));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceAlreadyExistsToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else if (errorStatus === 403) {
      dispatch(setError(AuthErrorKey.ResourceForbidden));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceForbiddenToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(setError(AuthErrorKey.DefaultErrorMessage));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    }
  } finally {
    if (!isStaleSetup(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const enrollSMSFactor = (
  t: TFunction,
  {
    label,
    countryCode,
    nationalNumber,
    loaLevel
  }: {
    label: string;
    countryCode: string;
    nationalNumber: string;
    loaLevel: number;
  }
): AppThunk<Promise<string | undefined>> => async (dispatch, getState): Promise<string | undefined> => {
  const gen = getState().setupSecurity.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));

  try {
    const userId = await dispatch(requireUserId() as any);

    const payload: Partial<ISecuritySetup> = {
      type: SecuritySetupType.SMS,
      label,
      options: {
        phoneNumber: {
          countryCode,
          nationalNumber
        }
      }
    } as any;

    const res = await Repos.instance.securitySetupRepository.create(payload, { userId }) as Partial<ISecuritySetup>;

    if (!res?.id) {
      throw new Error('Empty SMS enroll response');
    }

    if (isStaleSetup(gen, getState)) return undefined;

    dispatch(setSelectedFactor({
      factorId: res.id!,
      type: SecuritySetupType.SMS,
      digits: (res.options as any)?.digits ?? DEFAULT_SMS_DIGITS,
      loaLevel: res.level ?? loaLevel,
      data: (res.options as any)?.phoneNumber ?? ''
    }));

    return res.id!;
  } catch (err) {
    if (isStaleSetup(gen, getState)) return undefined;
    const errorStatus = getHttpErrorStatusCode(err) ?? '500';
    if (errorStatus === 409) {
      dispatch(setError(AuthErrorKey.ResourceAlreadyExists));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceAlreadyExistsToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else if (errorStatus === 403) {
      dispatch(setError(AuthErrorKey.ResourceForbidden));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceForbiddenToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(setError(AuthErrorKey.DefaultSMSErrorMessage));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    }
    return undefined;
  } finally {
    if (!isStaleSetup(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const enrollEmailFactor = (
  t: TFunction,
  { loaLevel }: { loaLevel: number }
): AppThunk<Promise<string | undefined>> => async (dispatch, getState) => {
  const gen = getState().setupSecurity.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));

  try {
    const userId = await dispatch(requireUserId() as any);

    const userEmail = getState().profile.profileDetails?.externalId;
    if (!userEmail) {
      dispatch(setError(AuthErrorKey.DefaultEmailErrorMessage));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.missingEmail', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
      return undefined;
    }

    const payload: Partial<ISecuritySetup> = { type: SecuritySetupType.EMAIL, email: userEmail } as any;

    const res = await Repos.instance.securitySetupRepository.create(payload, { userId }) as Partial<ISecuritySetup>;
    if (!res?.id) throw new Error('Empty Email enroll response');

    if (isStaleSetup(gen, getState)) return undefined;

    dispatch(setSelectedFactor({
      factorId: res.id!,
      type: SecuritySetupType.EMAIL,
      digits: (res.options as any)?.digits ?? DEFAULT_EMAIL_DIGITS,
      loaLevel: res.level ?? loaLevel
    }));

    try {
      await Repos.instance.securitySetupRepository.update({}, { userId, factorId: res.id!, action: 'challenge' });
    } catch {
      if (isStaleSetup(gen, getState)) return res.id!;
      dispatch(setError(AuthErrorKey.DefaultChallengeError));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.sendFailedToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    }

    return res.id!;
  } catch {
    if (isStaleSetup(gen, getState)) return undefined;
    dispatch(setError(AuthErrorKey.DefaultEmailErrorMessage));
    dispatch(showToast({
      title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'error',
      subtitle: ''
    }));
    return undefined;
  } finally {
    if (!isStaleSetup(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const elevateUserSession = (
  loaProof: Partial<ILoaEnrichment>,
  level: number,
  userId: string
): AppThunk => async (dispatch): Promise<void> => {
  dispatch(setError(''));
  dispatch(setEnrichmentStatus(EnrichmentStatus.Loading));
  dispatch(setEnrichmentError(undefined));
  dispatch(setEnrichmentReturnUrl(undefined));

  const { mode, applicationId, relativePath } = getFlowSnapshot();

  const payload: any = { ...loaProof };

  const isRedirectMode = mode === 'redirect';
  if (isRedirectMode) {
    if (applicationId) payload.applicationId = applicationId;
    if (relativePath) payload.relativePath = relativePath;
  }

  try {
    const res = await Repos.instance.loaEnrichmentRepository.update(payload, { id: userId });

    const returnUrl = res && typeof (res as any).returnUrl === 'string' && (res as any).returnUrl.trim()
      ? (res as any).returnUrl.trim()
      : undefined;

    if (isRedirectMode) {
      dispatch(setEnrichmentReturnUrl(returnUrl));

      if (!returnUrl) {
        dispatch(setEnrichmentStatus(EnrichmentStatus.Error));
        dispatch(setEnrichmentError('loaEnrichment.sessionResult.missingReturnUrl'));
        sessionStorage.setItem(ENRICHMENT_OUTCOME_KEY, JSON.stringify({
          status: 'error', returnUrl: undefined, errorKey: 'loaEnrichment.sessionResult.missingReturnUrl'
        }));
        return;
      }
    }

    dispatch(setEnrichmentStatus(EnrichmentStatus.Success));
    sessionStorage.setItem(ENRICHMENT_OUTCOME_KEY, JSON.stringify({
      status: 'success', returnUrl
    }));
    markElevationSuccess(level);
  } catch (error) {
    dispatch(setEnrichmentStatus(EnrichmentStatus.Error));
    sessionStorage.setItem(ENRICHMENT_OUTCOME_KEY, JSON.stringify({
      status: 'error', returnUrl: undefined, errorKey: undefined
    }));
    markElevationError(error);
  }
};

export const verifyOrActivateFactor = (
  t: TFunction,
  {
    factorId, code, action, successToastMsg, elevateLevel, label
  }: {
    factorId: string;
    code: string;
    action: VerifyAction;
    successToastMsg: string;
    elevateLevel: number;
    label?: string
  }
): AppThunk => async (dispatch, getState) => {
  const gen = getState().setupSecurity.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));
  const selected = getState().setupSecurity.selectedFactor;
  const factorType = selected?.type;
  try {
    const userId = await dispatch(requireUserId() as any);
    const payload: Partial<ISecuritySetup> = { code, label };

    const { search: currentSearch } = getState().router.location;
    const instance = new URLSearchParams(currentSearch || '').get('instance') || undefined;

    const pathParams: Record<string, string> = { userId, factorId, action };
    if (instance) pathParams.instance = instance;

    const res = await Repos.instance.securitySetupRepository.update(
      payload,
      pathParams
    );

    if (isStaleSetup(gen, getState)) return;

    const { search, hash } = getState().router.location;
    const nextSearch = removeQueryParam(search, 'smsFactorId');

    if (res?.loaProof) {
      dispatch(showToast({
        title: t(successToastMsg, { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'success',
        subtitle: ''
      }));
      dispatch(setEnrichmentStatus(EnrichmentStatus.Loading));
      dispatch(setEnrichmentError(undefined));
      dispatch(setEnrichmentContext({
        loaProof: res.loaProof,
        targetLevel: elevateLevel
      }));
      dispatch(replace({ pathname: SecuritySetupRoutes.enrichment, search: nextSearch, hash }));
    } else {
      dispatch(setEnrichmentStatus(EnrichmentStatus.Error));
      dispatch(setEnrichmentError('loaEnrichment.sessionResult.missingLoaProof'));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.missingTokenToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
      dispatch(replace({ pathname: SecuritySetupRoutes.enrichment, search: nextSearch, hash }));
    }
  } catch (err) {
    if (isStaleSetup(gen, getState)) return;
    const errorStatus = getHttpErrorStatusCode(err) ?? '500';
    if (factorType === SecuritySetupType.SMS) {
      if (action === 'activate') {
        if (errorStatus === 403) {
          dispatch(setValidationError(ValErr.InvalidSMSCode));
        } else if (errorStatus === 404) {
          const { search, hash } = getState().router.location;
          const nextSearch = removeQueryParam(search, 'smsFactorId');
          dispatch(replace({ pathname: SecuritySetupRoutes.enroll('sms'), search: nextSearch, hash }));
          dispatch(showToast({
            title: t('loaEnrichment.common.errors.otpExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
            timeout: 0,
            kind: 'error',
            subtitle: ''
          }));
        } else {
          dispatch(setError(AuthErrorKey.DefaultErrorMessage));
          dispatch(showToast({
            title: t('loaEnrichment.factors.sms.toast.activationFailed', { ns: I18nNamespaces.LOA_ENRICHMENT }),
            timeout: 0,
            kind: 'error',
            subtitle: ''
          }));
        }
      } else if (errorStatus === 403) {
        dispatch(setValidationError(ValErr.InvalidSMSCode));
      } else {
        dispatch(setError(AuthErrorKey.DefaultSMSVerifyError));
        dispatch(showToast({
          title: t('loaEnrichment.factors.sms.toast.verificationFailed', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      }
      return;
    }
    if (factorType === SecuritySetupType.EMAIL) {
      if (errorStatus === 403) {
        dispatch(setValidationError(ValErr.InvalidEmailCode));
      } else if (errorStatus === 404) {
        const { search, hash } = getState().router.location;
        dispatch(replace({ pathname: SecuritySetupRoutes.root, search, hash }));
        dispatch(showToast({
          title: t('loaEnrichment.common.errors.otpExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      } else {
        dispatch(showToast({
          title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      }
      return;
    }
    if (errorStatus === 403) {
      dispatch(setValidationError(ValErr.OtpInvalid));
    } else if (errorStatus === 404) {
      const { search, hash } = getState().router.location;
      const nextSearch = removeQueryParam(search, 'smsFactorId');

      dispatch(replace({ pathname: SecuritySetupRoutes.root, search: nextSearch, hash }));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.otpExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    }
  } finally {
    if (!isStaleSetup(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const selectFactorForVerify = (factorId: string): AppThunk => (dispatch, getState) => {
  const factor = getState().setupSecurity.enrolledFactors.find((f) => f.id === factorId);

  if (!factor) {
    dispatch(setSelectedFactor(undefined));
    return;
  }

  const derive = VERIFY_FACTOR_DATA_BY_TYPE[factor.type];
  const meta = derive ? derive({ factor }) : undefined;

  dispatch(setSelectedFactor(meta));

  dispatch(setValidationError(''));
  dispatch(setError(''));
};

export const challengeFactor = (
  t: TFunction,
  {
    factorId,
    factorType,
    pageFlow,
    errorKey,
    toastKey
  }: {
    factorId: string;
    factorType: SecuritySetupType;
    pageFlow?: ChallengePageFlow;
    errorKey?: string;
    toastKey?: string
  }
): AppThunk => async (dispatch, getState) => {
  const gen = getState().setupSecurity.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));
  try {
    const userId = await dispatch(requireUserId() as any);
    await Repos.instance.securitySetupRepository.update({}, { userId, factorId, action: 'challenge' });
    return true;
  } catch (err) {
    if (isStaleSetup(gen, getState)) return false;
    const errorStatus = getHttpErrorStatusCode(err) ?? '500';
    const { search, hash } = getState().router.location;

    if (errorStatus === 404) {
      if (factorType === SecuritySetupType.SMS) {
        if (pageFlow === 'sms-activate') {
          const nextSearch = removeQueryParam(search, 'smsFactorId');
          dispatch(replace({ pathname: SecuritySetupRoutes.enroll('sms'), search: nextSearch, hash }));
        } else {
          dispatch(replace({ pathname: SecuritySetupRoutes.root, search, hash }));
        }
      } else {
        dispatch(replace({ pathname: SecuritySetupRoutes.root, search, hash }));
      }

      dispatch(showToast({
        title: t('loaEnrichment.common.errors.challengeExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));

      return true;
    }

    dispatch(setError(errorKey ?? AuthErrorKey.DefaultChallengeError));
    dispatch(showToast({
      title: t(toastKey ?? 'loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'error',
      subtitle: ''
    }));
    return true;
  } finally {
    if (!isStaleSetup(gen, getState)) dispatch(setActionLoading(false));
  }
};

export default setupSecuritySlice.reducer;
